/**
 * The marketing screens' pure logic. No React, no fetch - so it can be tested
 * without either.
 *
 * `canon` is the load-bearing one. The audience editor pushes a rebuilt
 * audience object upwards on every change and has to answer "is this actually
 * different from what is saved" before marking the campaign dirty. A plain
 * `JSON.stringify` comparison cannot answer that: the saved copy has been
 * through Postgres `jsonb`, which does not preserve key order, so an unchanged
 * audience would come back as a different string and the form would sit
 * permanently dirty, offering to save a change nobody made.
 */
import { mktContactedTotal } from "@wa/core/marketing-ledger";
import type { Audience, Campaign, CampaignRow } from "../../../lib/marketing";
// Lives with the filter it prunes rather than here, because the register list
// builds a campaign audience too now - see `registerAudience` next to it.
// Re-exported so the nineteen files that import it from this module do not have
// to care where it moved to.
export { pruneFilter } from "../../../lib/audienceFilter";

/**
 * How many addresses this campaign has actually written to.
 *
 * ⚠ NOT `campaign.sent`. A recipient row holds one status and the later facts
 * overwrite the earlier ones, so `sent` means "went out and nothing has
 * happened since": a campaign that mailed fifty people and heard back from
 * three shows `sent: 47`. Read as the size of the send, that is a number which
 * goes DOWN as the campaign succeeds, and it did exactly that the day replies
 * started being recorded.
 *
 * The list of statuses lives in `@wa/core` so this and the server's own delete
 * guard cannot disagree about what "contacted" means.
 */
export function contactedTotal(campaign: Campaign | CampaignRow): number {
  return mktContactedTotal(campaign);
}

/** Stable serialisation: object keys sorted, `undefined` members dropped.
 *  Arrays keep their order, because in an audience they are a set the operator
 *  chose and re-ordering them would hide a real edit. */
export function canon(value: unknown): string {
  return JSON.stringify(value, (_key, val: unknown) => {
    if (!val || typeof val !== "object" || Array.isArray(val)) return val;
    return Object.fromEntries(
      Object.entries(val as Record<string, unknown>)
        .filter(([, v]) => v !== undefined)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)),
    );
  });
}

/** True when two audiences select the same thing, whatever order their keys
 *  came back in. `null` and an empty audience are NOT the same: one has never
 *  been set, the other selects the whole register. */
export function sameAudience(a: Audience | null | undefined, b: Audience | null | undefined): boolean {
  if (!a || !b) return !a && !b;
  return canon(a) === canon(b);
}

/**
 * Campaign name to slug. The accented letters this alphabet uses are folded to
 * ASCII explicitly rather than by `normalize` alone, because some of them are
 * distinct letters rather than accented vowels and stripping their diacritic
 * would give the wrong sound.
 */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[äå]/g, "a")
    .replace(/[öõø]/g, "o")
    .replace(/ü/g, "u")
    .replace(/š/g, "s")
    .replace(/ž/g, "z")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

/**
 * How long this pace takes to work through an audience, in whole days.
 *
 * The three pace fields are each individually sensible and together decide
 * something nobody states: a 200/day cap against a 20 000-address audience is a
 * hundred days of sending. That is a plan or a mistake depending on whether
 * anyone worked it out, so the screen works it out.
 */
export function daysToSend(addresses: number, dailyCap: number): number {
  if (addresses <= 0 || dailyCap <= 0) return 0;
  return Math.ceil(addresses / dailyCap);
}

/**
 * The most letters a day can physically hold at this gap.
 *
 * The minimum gap and the daily cap can quietly contradict each other: 60
 * seconds between letters allows 1 440 a day, so a 5 000 daily cap is not a
 * limit, it is a number that never applies. Whichever is smaller is the real
 * cap, and the form should say which one is doing the work.
 */
export function capacityPerDay(minGapSeconds: number): number {
  if (minGapSeconds <= 0) return Infinity;
  return Math.floor(86_400 / minGapSeconds);
}

/**
 * A deliverability rate, written out.
 *
 * The module's own `pct` helper is wrong for these numbers and it is worth
 * saying why rather than quietly using a second formatter: `pct` rounds to whole
 * percent and collapses everything under one into "<1%". The complaint limit is
 * 0.08%, so under that formatter a rate at the limit, a rate five times over it
 * and a rate a hundredth of it all print the same three characters.
 *
 * Two decimals, and a trailing zero kept: "2.40%" beside "2.00%" reads as a
 * column of comparable numbers, where "2.4%" and "2%" read as two different
 * kinds of measurement.
 */
export function rateText(rate: number): string {
  if (!Number.isFinite(rate) || rate <= 0) return "0.00%";
  return `${(rate * 100).toFixed(2)}%`;
}
